import axios from '@/component/axios'

const baseApi = () => (process.env.VUE_APP_APIENDPOINT || '').trim()
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
})
const multipartAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
})
const buildUrl = path => `${baseApi().replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`

const request = (method, path, config = {}) => axios({
  method,
  url: buildUrl(path),
  headers: authHeaders(),
  ...config,
})

/** Admin panel paths (`/api/v1/admin/*` + Sanctum `auth/admin/*`). */
export const adminPanelApi = {
  // Dashboard & ads moderation
  dashboard: () => request('get', 'admin/dashboard'),
  adsDashboard: params => request('get', 'admin/ads/dashboard', { params }),
  ads: params => request('get', 'admin/ads', { params }),
  adsPending: params => request('get', 'admin/ads/pending', { params }),
  adsMostClicked: params => request('get', 'admin/ads/most-clicked', { params }),
  approveAd: id => request('patch', `admin/ads/${id}/approve`),
  rejectAd: payload => request('post', 'admin/ads/reject', { data: payload }),
  createAd: formData => request('post', 'admin/ads', {
    data: formData,
    headers: multipartAuthHeaders(),
  }),
  updateAd: (id, formData) => {
    // Multipart PUT bodies are unreliable in PHP; spoof via POST + _method.
    if (formData instanceof FormData && !formData.has('_method')) {
      formData.append('_method', 'PUT')
    }
    return request('post', `admin/ads/${id}`, {
      data: formData,
      headers: multipartAuthHeaders(),
    })
  },
  uploadImage: formData => request('post', 'admin/upload/image', {
    data: formData,
    headers: multipartAuthHeaders(),
  }),
  uploadMedia: formData => request('post', 'admin/upload/media', {
    data: formData,
    headers: multipartAuthHeaders(),
  }),
  deleteAd: id => request('delete', `admin/ads/${id}/delete`),

  // Mobile app users (all rows in `users`, excluding panel `admins` emails)
  users: params => request('get', 'admin/reports/users', { params }),
  userDetails: id => request('get', `admin/users/details/${id}`),
  updateUser: (id, payload) => request('put', `admin/users/${id}`, { data: payload }),
  deleteUser: id => request('delete', `admin/users/delete/${id}`),
  blockUser: id => request('patch', `admin/users/block/${id}`),
  suspendUser: id => request('patch', 'admin/users/suspend', { data: { id } }),
  unsuspendUser: id => request('patch', 'admin/users/unsuspend', { data: { id } }),
  userReports: (userId, params) => request('get', `admin/users/reports/${userId}`, { params }),
  userContentPreferences: userId => request('get', `admin/users/${userId}/categories`),
  updateUserContentPreferences: (userId, categoryIds) => request('post', `admin/users/${userId}/categories`, {
    data: { id: userId, category_ids: categoryIds },
  }),
  resetUserContentPreferences: userId => request('delete', `admin/users/${userId}/categories/reset`, {
    params: { id: userId },
  }),
  followedCompanies: (userId, params) => request('get', `admin/users/followed-companies/${userId}`, { params }),
  userSavedAds: (userId, params) => request('get', `admin/users/${userId}/saved-ads`, { params }),
  userActivity: (userId, params) => request('get', `admin/users/${userId}/activity`, { params }),
  unfollowCompany: (userId, advertiserId) => request(
    'delete',
    `admin/users/followed-companies/delete/${userId}/${advertiserId}`,
  ),

  // Advertisers / vendors (rows in `vendors`; details keyed by vendor id or owner user id)
  advertisers: params => request('get', 'admin/reports/advertisers', { params }),
  advertiserDetails: id => request('get', `admin/advertisers/details/${id}`),
  createAdvertiser: payload => request('post', 'admin/advertisers', { data: payload }),
  updateAdvertiser: (id, payload) => request('put', `admin/advertisers/${id}`, { data: payload }),
  changeAdvertiserPassword: (id, payload) => request('patch', `admin/advertisers/${id}/password`, { data: payload }),
  advertiserFollowers: (id, params) => request('get', `admin/advertisers/details/${id}/followers`, { params }),
  advertiserAds: params => request('get', 'admin/advertisers/ads', { params }),
  deleteAdvertiser: id => request('delete', 'admin/advertisers/delete', { params: { id } }),
  approveAdvertiser: id => request('patch', `admin/advertisers/${id}/approve`),
  rejectAdvertiser: payload => request('post', 'admin/advertisers/reject', { data: payload }),
  suspendAdvertiser: payload => request('patch', 'admin/advertisers/suspend', { data: payload }),
  unsuspendAdvertiser: id => request('patch', 'admin/advertisers/unsuspend', { data: { id } }),
  advertiserActivity: (id, payload) => request('post', `admin/advertisers/details/${id}/activity`, { data: payload }),

  // Admin staff (`admins` table — not `users`)
  staff: params => request('get', 'admin/staff', { params }),
  staffMember: id => request('get', `admin/staff/${id}`),
  staffRoles: () => request('get', 'admin/staff/roles'),
  createStaff: payload => request('post', 'admin/staff', { data: payload }),
  updateStaff: (id, payload) => request('put', `admin/staff/${id}`, { data: payload }),
  deleteStaff: id => request('delete', `admin/staff/${id}`),

  // Reports hub
  reportsDashboard: () => request('get', 'admin/reports/dashboard'),
  reportsSuspendedUsers: params => request('get', 'admin/reports/suspended-users', { params }),
  reportsSuspendedAdvertisers: params => request('get', 'admin/reports/suspended-advertisers', { params }),
  reportsReportedAds: params => request('get', 'admin/reports/reported-ads', { params }),
  reportsRejectedAds: params => request('get', 'admin/reports/rejected-ads', { params }),
  reportsAds: params => request('get', 'admin/reports/ads', { params }),
  reportsAdvertisers: params => request('get', 'admin/reports/advertisers', { params }),
  reportsUsers: params => request('get', 'admin/reports/users', { params }),
  deleteReport: id => request('delete', `admin/reports/delete/${id}`),

  // Notifications, CMS, plans, profile
  notifications: params => request('get', 'admin/notifications', { params }),
  notificationsMarkRead: payload => request('patch', 'admin/notifications/read', { data: payload }),
  registerDeviceToken: payload => request('post', 'admin/device-token', { data: payload }),
  removeDeviceToken: payload => request('delete', 'admin/device-token', { data: payload }),
  brands: params => request('get', 'admin/brands', { params }),
  createBrand: payload => request('post', 'admin/brands', { data: payload }),
  updateBrand: (id, payload) => request('put', `admin/brands/${id}`, { data: payload }),
  deleteBrand: id => request('delete', `admin/brands/${id}`),
  categories: () => request('get', 'admin/categories'),
  createCategory: payload => request('post', 'admin/categories', { data: payload }),
  updateCategory: (id, payload) => request('put', `admin/categories/${id}`, { data: payload }),
  deleteCategory: id => request('delete', `admin/categories/${id}`),
  categoryRequests: params => request('get', 'admin/category-requests', { params }),
  approveCategoryRequest: id => request('patch', `admin/category-requests/${id}/approve`),
  rejectCategoryRequest: (id, payload = {}) => request('patch', `admin/category-requests/${id}/reject`, { data: payload }),

  plans: params => request('get', 'admin/plans', { params }),
  plan: id => request('get', `admin/plans/${id}`),
  createPlan: payload => request('post', 'admin/plans', { data: payload }),
  updatePlan: payload => request('put', 'admin/plans/update', { data: payload }),
  deletePlan: id => request('delete', `admin/plans/${id}`),

  pages: params => request('get', 'admin/pages', { params }),
  page: id => request('get', `admin/pages/${id}`),
  createPage: payload => request('post', 'admin/pages', { data: payload }),
  updatePage: (id, payload) => request('put', `admin/pages/${id}`, { data: payload }),
  savePage: (payload, isMultipart = false) => request('post', 'admin/pages', {
    data: payload,
    headers: isMultipart ? multipartAuthHeaders() : authHeaders(),
  }),
  deletePage: id => request('delete', `admin/pages/${id}`),
  downloadDatabaseBackup: () => request('get', 'admin/database/backup', { responseType: 'blob' }),
  setupSqlInfo: () => request('get', 'admin/setup/sql'),
  setupSqlQuery: sql => request('post', 'admin/setup/sql/query', { data: { sql } }),
  setupRequestLogs: params => request('get', 'admin/setup/request-logs', { params }),
  setupRequestLog: id => request('get', `admin/setup/request-logs/${id}`),
  setupStorageHealth: () => request('get', 'admin/setup/storage/health'),

  adReview: id => request('get', `admin/ads/review/${id}`),
  adPreview: id => request('get', `admin/ads/preview/${id}`),

  adminProfile: id => request('get', `auth/admin/get-profile/${id}`),
  updateAdminProfile: (id, payload) => request('put', `auth/admin/update/${id}`, { data: payload }),
}

export const reportRouteApiMap = {
  'reports-reported-by-users': 'reportsReportedAds',
  'reports-suspended-advertisers': 'reportsSuspendedAdvertisers',
  'reports-suspended-users': 'reportsSuspendedUsers',
  'reports-users': 'reportsUsers',
  'reports-reject-ads': 'reportsRejectedAds',
  'reports-ads': 'reportsAds',
  'reports-advertisers': 'reportsAdvertisers',
}

export default adminPanelApi
